import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const DISMISS_KEY = 'mb_dismissed_banner';

// Maps a route pathname to the "page" key used by the admin banner page-visibility checkboxes.
function pageKeyForPath(pathname) {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/real-estate')) return 'real-estate';
  if (pathname.startsWith('/commercial')) return 'commercial';
  if (pathname.startsWith('/social-media')) return 'social-media';
  if (pathname.startsWith('/reviews')) return 'reviews';
  if (pathname.startsWith('/contact')) return 'contact';
  if (pathname.startsWith('/portal')) return 'portal';
  return null; // admin, /cancel/:token, 404, etc. never show the banner
}

const TYPE_STYLES = {
  info: {
    bg: 'bg-teal-700',
    icon: 'ri-information-line',
  },
  warning: {
    bg: 'bg-amber-600',
    icon: 'ri-error-warning-line',
  },
  urgent: {
    bg: 'bg-rose-700',
    icon: 'ri-alarm-warning-line',
  },
};

export default function AnnouncementBanner() {
  const location = useLocation();
  const [banner, setBanner] = useState(null);
  const [dismissedId, setDismissedId] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) || '';
    } catch (_) {
      return '';
    }
  });
  const barRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/banner`);
        if (res.ok) {
          const data = await res.json();
          setBanner(data);
        }
      } catch (_) {
        // Silently ignore - a broken banner fetch should never block the site.
      }
    };
    load();
  }, []);

  const pageKey = pageKeyForPath(location.pathname);
  const bannerId = banner ? `${banner.updated_at || ''}|${banner.message || ''}` : '';
  const isVisible = Boolean(
    banner &&
    banner.enabled &&
    banner.message &&
    pageKey &&
    (banner.pages || []).includes(pageKey) &&
    !(banner.dismissible && dismissedId === bannerId)
  );

  const updateHeightVar = useCallback((height) => {
    document.documentElement.style.setProperty('--announcement-banner-height', `${height}px`);
  }, []);

  useEffect(() => {
    if (!isVisible) {
      updateHeightVar(0);
      return;
    }
    const el = barRef.current;
    if (!el) return;
    updateHeightVar(el.offsetHeight);
    const observer = new ResizeObserver(() => updateHeightVar(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [isVisible, updateHeightVar]);

  // Ensure the CSS var is cleared if the component unmounts entirely.
  useEffect(() => () => updateHeightVar(0), [updateHeightVar]);

  if (!isVisible) return null;

  const style = TYPE_STYLES[banner.type] || TYPE_STYLES.info;

  const handleDismiss = () => {
    setDismissedId(bannerId);
    try {
      localStorage.setItem(DISMISS_KEY, bannerId);
    } catch (_) {
      /* ignore storage errors (private browsing, etc.) */
    }
  };

  return (
    <div
      ref={barRef}
      data-testid="announcement-banner"
      className={`fixed top-0 left-0 w-full z-[70] ${style.bg} text-white`}
    >
      <div className="container mx-auto px-4 py-2.5 flex items-center gap-3">
        <i className={`${style.icon} text-lg shrink-0`}></i>
        <p className="flex-1 text-sm font-medium text-center sm:text-left" data-testid="announcement-banner-message">
          {banner.message}
        </p>
        {banner.dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss announcement"
            data-testid="announcement-banner-dismiss"
            className="shrink-0 p-1.5 rounded-md hover:bg-white/15 transition-colors"
          >
            <i className="ri-close-line text-lg pointer-events-none"></i>
          </button>
        )}
      </div>
    </div>
  );
}
